import FieldDrawer, { type GroupTarget } from '@components/Customization/PoiTypes/FieldDrawer';
import GroupDrawer from '@components/Customization/PoiTypes/GroupDrawer';
import PoiTypeDrawer, { type PoiTypeDraft } from '@components/Customization/PoiTypes/PoiTypeDrawer';
import PoiTypePreview from '@components/Customization/PoiTypes/PoiTypePreview';
import PoiTypeSchema from '@components/Customization/PoiTypes/PoiTypeSchema';
import ShareDrawer from '@components/Customization/PoiTypes/ShareDrawer';
import DeleteConfirmationDialog from '@components/Shared/Dialog/DeleteConfirmationDialog';
import Header from '@components/Shared/Layout/Header';
import { ROUTE_PATHS } from '@constants/routes';
import { useCustomizationLocales } from '@lib/customization/useCustomizationLocales';
import { useLocalization } from '@lib/localization';
import { has } from '@lib/permission';
import { Box, Button, IconButton, LinearProgress, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { backButtonStyles, dangerHoverStyles, detailIconStyles } from '@pages/CustomPoiTypes.styles';
import {
  resolveLabel, toPoiTypeBody, useAddPoiTypeMutation, useDeletePoiTypeMutation, useDeletePoiTypeShareMutation,
  useGetEffectivePoiTypeQuery, useGetPoiTypesQuery, useSharePoiTypeMutation, useUpdatePoiTypeMutation,
  type PoiField, type PoiFieldGroup, type PoiType, type PoiTypeBody,
} from '@store/api/poiTypeEndpoints';
import { useGetTeamDirectoryQuery } from '@store/api/teamEndpoints';
import { usePermission } from '@store/hooks';
import { useEffect, useState } from 'react';
import {
  AngleLeftIcon, CopyIcon, EditIcon, EyeIcon, MapMarkerIcon, ShareAltIcon, TrashIcon,
} from 'react-line-awesome';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

/** Which deletion is waiting on the confirmation dialog. */
type PendingDelete =
  | { kind: 'field'; group: GroupTarget; index: number }
  | { kind: 'group'; index: number }
  | { kind: 'type' }
  | null;

type DrawerState =
  | { kind: 'none' }
  | { kind: 'type' }
  | { kind: 'group'; index: number | null }
  | { kind: 'field'; group: GroupTarget; index: number | null }
  | { kind: 'share' };

/**
 * One POI type and everything that can be done to it.
 *
 * The list stays a list — this page owns every mutation, the same split the teams pages
 * use. Reading the whole catalogue to find one type is deliberate: it is the query the
 * list already holds, so arriving here from a row costs no extra request.
 */
const PoiTypeDetailPage = () => {
  const { t } = useLocalization();
  const locales = useCustomizationLocales();
  const navigate = useNavigate();
  const { poiTypeId } = useParams<{ poiTypeId: string }>();
  const { canInAnyTeam } = usePermission();

  const [previewOpen, setPreviewOpen] = useState(false);

  const { data: poiTypes = [], isLoading } = useGetPoiTypesQuery();

  // Only fetched when the preview is actually opened: the page itself renders from the
  // stored type, and a standalone type would otherwise pay for a resolve it never shows.
  // `currentData` rather than `data` for the same reason `PoiTypeSchema` uses it — `data`
  // holds the last result for any argument, so it can belong to a different type.
  const { currentData: resolved, isFetching: resolving } = useGetEffectivePoiTypeQuery(
    poiTypeId ?? '', { skip: !previewOpen || !poiTypeId },
  );

  // The directory only feeds the share drawer's team picker, and asking for it needs
  // `folder:share` or `poi_type:post` — neither of which an INSPECTOR or ANALYST has, so
  // fetching it unconditionally met them with a 403 the moment they opened a type.
  const { data: teamDirectory = [] } = useGetTeamDirectoryQuery(undefined, {
    skip: !canInAnyTeam('poi_type:share'),
  });

  const [addPoiType, { isLoading: isCreating }] = useAddPoiTypeMutation();
  const [updatePoiType, { isLoading: isUpdating }] = useUpdatePoiTypeMutation();
  const [deletePoiType] = useDeletePoiTypeMutation();
  const [sharePoiType, { isLoading: isSharing }] = useSharePoiTypeMutation();
  const [deletePoiTypeShare, { isLoading: isRevoking }] = useDeletePoiTypeShareMutation();

  const [drawer, setDrawer] = useState<DrawerState>({ kind: 'none' });
  const [deleting, setDeleting] = useState<PendingDelete>(null);

  // Moving between types keeps this page mounted, so anything left open would carry the
  // group and field indices of the type we just left — and a pending delete would then
  // confirm against the wrong one.
  useEffect(() => {
    setDrawer({ kind: 'none' });
    setDeleting(null);
    setPreviewOpen(false);
  }, [poiTypeId]);

  const poiType = poiTypes.find((candidate) => candidate._id === poiTypeId);
  const close = () => setDrawer({ kind: 'none' });
  const backToList = () => navigate(ROUTE_PATHS.CUSTOMIZATIONS_POI_TYPES);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Header />
        <LinearProgress sx={{ width: '100%' }} />
      </Box>
    );
  }

  if (!poiType) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Header />
        <Box sx={{ width: '100%' }}>
          <Button onClick={backToList} startIcon={<AngleLeftIcon />} color="inherit" sx={backButtonStyles}>
            {t('Customization_BackToPoiTypes')}
          </Button>
          <Typography color="text.secondary">{t('Customization_PoiTypeNotFound')}</Typography>
        </Box>
      </Box>
    );
  }

  const canEdit = has(poiType, 'poi_type:put');
  const canDelete = has(poiType, 'poi_type:delete');
  const canShare = has(poiType, 'poi_type:share');
  const saving = isUpdating || isCreating;

  const parent = poiType.inheritFrom
    ? poiTypes.find((candidate) => candidate._id === poiType.inheritFrom)
    : undefined;

  const fieldCount = poiType.fields.length
    + poiType.groups.reduce((total, group) => total + group.fields.length, 0);

  /**
   * Every edit is a full replace — the API has no per-field endpoint, because `version`
   * can only answer "is my copy stale" if each edit lands as one write. The version read
   * is sent back, so an edit that landed in between is a 412 rather than a silent
   * overwrite.
   */
  const saveType = async (changes: Partial<PoiTypeBody>) => {
    const body = toPoiTypeBody({
      name: poiType.name,
      description: poiType.description,
      inheritFrom: poiType.inheritFrom,
      fields: poiType.fields,
      groups: poiType.groups,
      ...changes,
    });

    const result = await updatePoiType({ id: poiType._id, version: poiType.version, ...body });

    if (!result.error) close();
  };

  const fieldsOf = (group: GroupTarget): PoiField[] =>
    group === 'd' ? poiType.fields : poiType.groups[group].fields;

  const withFields = (source: PoiType, group: GroupTarget, fields: PoiField[]): Partial<PoiTypeBody> =>
    group === 'd'
      ? { fields }
      : { groups: source.groups.map((current, index) => (index === group ? { ...current, fields } : current)) };

  const handleSaveField = async (field: PoiField, target: GroupTarget) => {
    if (drawer.kind !== 'field') return;

    const { group, index } = drawer;

    // Moving a field between groups is two edits to one document: drop it where it was,
    // append it where it goes. Both have to be in the single body we send.
    if (index !== null && target !== group) {
      const source = fieldsOf(group).filter((_, i) => i !== index);
      const afterRemoval = { ...poiType, ...withFields(poiType, group, source) } as PoiType;
      const targetFields = target === 'd' ? afterRemoval.fields : afterRemoval.groups[target].fields;

      await saveType({
        ...withFields(poiType, group, source),
        ...withFields(afterRemoval, target, [...targetFields, field]),
      });
      return;
    }

    const current = fieldsOf(target);
    const next = index === null
      ? [...current, field]
      : current.map((existing, i) => (i === index ? field : existing));

    await saveType(withFields(poiType, target, next));
  };

  const handleSaveGroup = async (group: PoiFieldGroup) => {
    if (drawer.kind !== 'group') return;

    const { index } = drawer;
    const groups = index === null
      ? [...poiType.groups, group]
      : poiType.groups.map((existing, i) => (i === index ? { ...existing, ...group } : existing));

    await saveType({ groups });
  };

  const handleSaveType = async (draft: PoiTypeDraft) => {
    await saveType({ name: draft.name, description: draft.description, inheritFrom: draft.inheritFrom });
  };

  /**
   * What a confirmed deletion will do, and what to say it is about.
   *
   * Held as one piece of state so the three deletions share the app's own confirmation
   * dialog instead of `window.confirm`, and so the dialog never has to work out which of
   * them it is standing in for.
   */
  const pendingDelete = ((): { subtitle: string; confirm: () => Promise<void> } | null => {
    if (deleting === null) return null;

    if (deleting.kind === 'field') {
      const { group, index } = deleting;
      const field = fieldsOf(group)[index];

      return {
        subtitle: t('Customization_ConfirmDeleteField', [resolveLabel(field.label, locales)]),
        confirm: () => saveType(withFields(poiType, group, fieldsOf(group).filter((_, i) => i !== index))),
      };
    }

    if (deleting.kind === 'group') {
      const { index } = deleting;

      return {
        subtitle: t('Customization_ConfirmDeleteGroup', [resolveLabel(poiType.groups[index].name, locales)]),
        confirm: () => saveType({ groups: poiType.groups.filter((_, i) => i !== index) }),
      };
    }

    return {
      subtitle: t('Customization_ConfirmDeleteType', [resolveLabel(poiType.name, locales)]),
      confirm: async () => {
        const result = await deletePoiType(poiType._id);

        if (!result.error) backToList();
      },
    };
  })();

  /**
   * A duplicate is a fresh create carrying the same content: only the primary locale of
   * the name is marked, so the copy is recognisable without inventing translations for
   * languages the author has not filled in.
   */
  const handleDuplicate = async () => {
    const result = await addPoiType({
      teamId: poiType.teamId,
      ...toPoiTypeBody({
        name: {
          ...poiType.name,
          [locales.primary]: `${resolveLabel(poiType.name, locales)} ${t('Customization_CopySuffix')}`,
        },
        description: poiType.description,
        inheritFrom: poiType.inheritFrom,
        fields: poiType.fields,
        groups: poiType.groups,
      }),
    });

    if (!result.error && 'data' in result && result.data) {
      navigate(generatePath(ROUTE_PATHS.CUSTOMIZATIONS_POI_TYPE, { poiTypeId: result.data.id }));
    }
  };

  /** Header actions are icons here, the same shape the team detail header uses. */
  const action = (
    title: string, allowed: boolean, icon: React.ReactNode, onClick: () => void, danger = false
  ) => (
    <Tooltip title={allowed ? title : t('Permission_NotAllowed')} arrow>
      <span>
        <IconButton
          onClick={onClick}
          disabled={!allowed}
          sx={danger ? dangerHoverStyles : { color: 'text.secondary' }}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Header />

      <Box sx={{ width: '100%' }}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 3 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box sx={detailIconStyles}>
              <MapMarkerIcon />
            </Box>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>{resolveLabel(poiType.name, locales)}</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>{poiType.description}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                {t('Customization_TypeSummary', [poiType.groups.length, fieldCount])}
                {` · v${poiType.version}`}
              </Typography>
            </Box>

            <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
              {/* Ungated: a preview only reads, so anyone who can open the type can see
                  how it lands on a device. */}
              {action(t('Customization_Preview'), true, <EyeIcon />, () => setPreviewOpen(true))}
              {action(t('Common_Edit'), canEdit, <EditIcon />, () => setDrawer({ kind: 'type' }))}
              {action(t('Customization_Duplicate'), canEdit, <CopyIcon />, () => void handleDuplicate())}
              {action(t('Customization_ShareType'), canShare, <ShareAltIcon />, () => setDrawer({ kind: 'share' }))}
              {action(t('Common_Delete'), canDelete, <TrashIcon />, () => setDeleting({ kind: 'type' }), true)}
            </Stack>
          </Stack>
        </Paper>

        <PoiTypeSchema
          poiType={poiType}
          parentName={parent && resolveLabel(parent.name, locales)}
          onOpenParent={parent
            ? () => navigate(generatePath(ROUTE_PATHS.CUSTOMIZATIONS_POI_TYPE, { poiTypeId: parent._id }))
            : undefined}
          onAddField={(group) => setDrawer({ kind: 'field', group, index: null })}
          onEditField={(group, index) => setDrawer({ kind: 'field', group, index })}
          onDeleteField={(group, index) => setDeleting({ kind: 'field', group, index })}
          onAddGroup={() => setDrawer({ kind: 'group', index: null })}
          onEditGroup={(index) => setDrawer({ kind: 'group', index })}
          onDeleteGroup={(index) => setDeleting({ kind: 'group', index })}
        />
      </Box>

      <PoiTypePreview
        open={previewOpen}
        items={resolved?.id === poiType._id ? resolved.items : undefined}
        typeName={resolveLabel(poiType.name, locales)}
        loading={resolving}
        onClose={() => setPreviewOpen(false)}
      />

      <PoiTypeDrawer
        open={drawer.kind === 'type'}
        poiType={poiType}
        parentCandidates={poiTypes.filter((candidate) => candidate._id !== poiType._id)}
        teams={[]}
        saving={saving}
        onClose={close}
        onSave={handleSaveType}
      />

      <GroupDrawer
        open={drawer.kind === 'group'}
        group={drawer.kind === 'group' && drawer.index !== null ? poiType.groups[drawer.index] : null}
        typeName={resolveLabel(poiType.name, locales)}
        saving={saving}
        onClose={close}
        onSave={handleSaveGroup}
      />

      <FieldDrawer
        open={drawer.kind === 'field'}
        field={drawer.kind === 'field' && drawer.index !== null ? fieldsOf(drawer.group)[drawer.index] : null}
        group={drawer.kind === 'field' ? drawer.group : 'd'}
        groupNames={poiType.groups.map((group) => resolveLabel(group.name, locales))}
        typeName={resolveLabel(poiType.name, locales)}
        saving={saving}
        onClose={close}
        onSave={handleSaveField}
      />

      <DeleteConfirmationDialog
        open={pendingDelete !== null}
        warningSubtitle={pendingDelete?.subtitle}
        onClose={() => setDeleting(null)}
        onConfirm={() => pendingDelete?.confirm() ?? Promise.resolve()}
      />

      <ShareDrawer
        open={drawer.kind === 'share'}
        poiType={poiType}
        teams={teamDirectory}
        saving={isSharing || isRevoking}
        onClose={close}
        onShare={(teamId) => sharePoiType({ id: poiType._id, teamIds: [teamId] })}
        onRevoke={(teamId) => deletePoiTypeShare({ id: poiType._id, teamId })}
      />
    </Box>
  );
};

export default PoiTypeDetailPage;
